import React from 'react';
import { Calendar, Search, X, AlertCircle } from 'lucide-react';

interface FilterPanelProps {
  fromDate: Date;
  toDate: Date;
  supplierNo: string;
  orderNoERP: string;
  dateError: string | null;
  onFromDateChange: (date: Date) => void;
  onToDateChange: (date: Date) => void;
  onSupplierNoChange: (value: string) => void;
  onOrderNoERPChange: (value: string) => void;
  onClearFilters: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  fromDate,
  toDate,
  supplierNo,
  orderNoERP,
  dateError,
  onFromDateChange,
  onToDateChange,
  onSupplierNoChange,
  onOrderNoERPChange,
  onClearFilters
}) => {
  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse input date
  const parseDateFromInput = (dateStr: string): Date => {
    return new Date(dateStr);
  };

  // Quick filter buttons
  const setQuickFilter = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    onFromDateChange(from);
    onToDateChange(to);
  };

  const hasActiveFilters = supplierNo.trim() !== '' || orderNoERP.trim() !== '';

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Date range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="inline-block mr-1" size={14} />
            Fra dato
          </label>
          <input
            type="date"
            value={formatDateForInput(fromDate)}
            onChange={(e) => onFromDateChange(parseDateFromInput(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            max={formatDateForInput(toDate)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="inline-block mr-1" size={14} />
            Til dato
          </label>
          <input
            type="date"
            value={formatDateForInput(toDate)}
            onChange={(e) => onToDateChange(parseDateFromInput(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            min={formatDateForInput(fromDate)}
            max={formatDateForInput(new Date())}
          />
        </div>

        {/* Supplier filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Search className="inline-block mr-1" size={14} />
            Leverandørnr
          </label>
          <input
            type="text"
            value={supplierNo}
            onChange={(e) => onSupplierNoChange(e.target.value)}
            placeholder="Søk leverandør..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Order number filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Search className="inline-block mr-1" size={14} />
            Ordrenummer (ERP)
          </label>
          <input
            type="text"
            value={orderNoERP}
            onChange={(e) => onOrderNoERPChange(e.target.value)}
            placeholder="Søk ordrenr..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Quick filter buttons */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-600 mr-2">Hurtigfiltre:</span>
        <button
          onClick={() => setQuickFilter(7)}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Siste 7 dager
        </button>
        <button
          onClick={() => setQuickFilter(14)}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Siste 14 dager
        </button>
        <button
          onClick={() => setQuickFilter(30)}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Siste 30 dager
        </button>
        <button
          onClick={() => setQuickFilter(60)}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Siste 60 dager
        </button>
        <button
          onClick={() => setQuickFilter(90)}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Siste 90 dager
        </button>

        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="ml-auto px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center"
          >
            <X className="mr-1" size={14} />
            Nullstill filtre
          </button>
        )}
      </div>

      {/* Date error */}
      {dateError && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
          <AlertCircle className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
          <p className="text-yellow-800 text-sm">{dateError}</p>
        </div>
      )}
    </div>
  );
};
